import type { AuthMethod } from "../../services/auth";
import { fetchAuthMethods, unlinkAuthMethod, providerFromAccountKey } from "../../services/auth";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonIcon,
  IonItem,
  IonText,
  IonLabel,
  useIonRouter,
  IonToast,
  IonSpinner,
} from "@ionic/react";

import {
  chevronForward,
  exitOutline,
  mail,
  phonePortraitOutline,
} from "ionicons/icons";
import "./Settings.css"; // Assuming you have a CSS file for styles
import Header from "../../components/Header";
import useAuth from "../../hooks/useAuth";
import { GoogleIcon, FacebookIcon, AppleIcon } from "../../assets/icons";
import { softDeleteAccount, updateProfile } from "../../services/user";
import {
  clearCurrentTabData,
  removeIonicStorage,
} from "../../services/storageService";
import { useStorage } from "../../contexts/StorageContext";
import { RequestPopup } from "../../components/layout/Guest/Components/RequestPopup";
import { formatPhoneNumber } from "../../utils/helpers";
import { RemoveList as keys } from "../../utils/helpers";
const MyAccountPage: React.FC = () => {
  // State to store data (simulating data from server)
  const [isLoading, setIsLoading] = useState(false);
  const { user, updateUserProfile } = useAuth();
  const [profile, setProfile] = useState<any>(user);
  useEffect(() => {
    if (user) setProfile(user);
  }, [user]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showDisconnectAlert, setShowDisconnectAlert] = useState(false);
  const [connectId, setConnectId] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const { storage, isStorageReady } = useStorage();
  const [actionLoading, setActionLoading] = useState<{
    [key: string]: boolean;
  }>({});

  const router = useIonRouter();
  const { logout, signInWithSocial } = useAuth();

  // Event handlers
  // const handleLogout = async () => {
  //   // router.push("/signin", "forward", "replace");

  //   await logout();
  // };
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);

  // ⚠️ The KERNEL is the truth about which sign-in methods exist — it owns `auth_identity`,
  // which is what a social login actually resolves against. blaze's
  // `profile.linked_accounts.<p>_account.connected` is a flag no server ever enforced, so
  // reading it here would keep showing "connected" after a successful unlink. See D37.
  const [authMethods, setAuthMethods] = useState<AuthMethod[]>([]);

  const refreshAuthMethods = useCallback(async () => {
    try {
      setAuthMethods(await fetchAuthMethods());
    } catch (error) {
      console.error("Could not load sign-in methods", error);
    }
  }, []);

  useEffect(() => {
    refreshAuthMethods();
  }, [refreshAuthMethods]);

  /** Is this provider currently a usable sign-in method? */
  const isMethodLinked = (accountKey: string): boolean =>
    authMethods.some((m) => m.type === providerFromAccountKey(accountKey));

  /**
   * blaze's legacy view: a provider connected BEFORE the kernel owned identities.
   *
   * ⚠️ These links are real — one test profile has had Facebook connected since April —
   * but they cannot be migrated. `auth_identity` is keyed on the COGNITO sub, and blaze
   * only ever stored the provider's profile fields (name, email, timestamps): no provider
   * id, no Cognito sub. That sub does not exist until the person signs in through Hosted
   * UI, because Cognito creates the federated user at that moment. So there is nothing to
   * backfill from — the data required has never existed.
   */
  const isLinkedInBlaze = (accountKey: string): boolean =>
    profile?.linked_accounts?.[
      accountKey as "facebook_account" | "google_account" | "apple_account"
    ]?.connected === true;

  /**
   * THREE states, not two — the parallel run makes "linked" ambiguous:
   *
   *   kernel knows it   -> a usable method the kernel can also REMOVE
   *   blaze only        -> genuinely connected, and it still signs you in (the kernel's
   *                        email-link fallback creates the row on first use) — but the
   *                        kernel holds no row, so Disconnect would 404
   *   neither           -> never connected
   *
   * Saying "not linked, link it to sign in" to someone in the middle state tells them to do
   * something they did months ago and can already use. This collapses back to two states on
   * its own at blaze EOL, when the middle one stops occurring.
   */
  const describeLink = (accountKey: string, label: string): string => {
    if (isMethodLinked(accountKey)) {
      return `Your ${label} account is linked and may be used to sign in to Arc Connect.`;
    }
    if (isLinkedInBlaze(accountKey)) {
      return `Your ${label} account is connected. Sign in with ${label} once to manage it here.`;
    }
    return `Your ${label} account is not linked. Link it to sign in with ${label}.`;
  };

  /** The kernel can only act on what it knows, so the middle state offers sign-in, not connect. */
  const linkActionLabel = (accountKey: string, label: string): string =>
    isLinkedInBlaze(accountKey) ? `Sign in with ${label} to manage` : `Connect ${label}`;


  const removeIonic = async () => {
    if (storage && isStorageReady) {
      await clearCurrentTabData(storage);
      for (const key of keys) {
        await removeIonicStorage(storage, key);
      }
    }
  };

  const handleLogout = () => {
    setShowLogoutAlert(true);
  };

  const confirmLogout = async () => {
    setShowLogoutAlert(false);
    // Clear any session data or cache if needed
    await removeIonic();
    // router.push("/signin", "forward", "replace");
    await logout();
  };

  const cancelLogout = () => {
    setShowLogoutAlert(false);
  };
  const handleDisconnectAccount = (accountId: string) => {
    setConnectId(accountId);
    setShowDisconnectAlert(true);
  };

  const confirmDisconnectAccount = async () => {
    setShowDisconnectAlert(false);

    setActionLoading((prev) => ({ ...prev, [connectId]: true }));
    try {
      // ⚠️ Was a CLIENT-SIDE profile edit: it built `linked_accounts` with
      // `connected: false` and PUT the whole profile back. No dedicated endpoint, no server
      // check, and after D36 removed the one client-side reader, nothing enforced it at all
      // — the button wrote a flag nobody read. The kernel deletes the `auth_identity` row
      // instead, so the method is gone by construction. See D37.
      await unlinkAuthMethod(providerFromAccountKey(connectId));

      // ⚠️ The kernel revokes this session when you unlink the method you SIGNED IN with
      // (and only then): removing the federated identity removes the one row that can
      // resolve a token carrying that provider's Cognito sub. So the refresh below is not
      // just a UI update — it is how we learn whether we are still authenticated. Deriving
      // it from the server's answer beats guessing from a locally-remembered login method,
      // which can drift.
      try {
        setAuthMethods(await fetchAuthMethods());
      } catch (err: any) {
        if (err?.response?.status === 401) {
          showToastMessage("Disconnected. Please sign in again.");
          await logout();
          return;
        }
        // Unlink succeeded; only the list refresh failed. Say so honestly rather than
        // reporting a failure that did not happen.
        console.error("Disconnected, but the sign-in method list could not be refreshed", err);
      }
      showToastMessage("Account disconnected successfully");
    } catch (error: any) {
      // The case that actually matters: the kernel refuses to leave an account with no way
      // back in. For anyone who signed up THROUGH a social provider, that identity is their
      // only one — so this is a normal outcome, not an edge case, and a generic
      // "Failed to disconnect" would leave them with no idea why.
      const code = error?.response?.data?.code;
      showToastMessage(
        code === "LAST_METHOD_CANNOT_BE_REMOVED"
          ? "This is your only way to sign in. Add another sign-in method first."
          : "Failed to disconnect account"
      );
      console.error(error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [connectId]: false }));
    }
  };

  const handleReconnectAccount = async (
    accountKey: "facebook_account" | "google_account" | "apple_account"
  ) => {
    setActionLoading((prev) => ({ ...prev, [accountKey]: true }));
    try {
      // ⚠️ Was a flag flip on `linked_accounts`, which re-linked nothing. Under D37 the way
      // back is simply to sign in with the provider again: the kernel's email-link fallback
      // returns the same Cognito sub and re-creates the identity. So "Reconnect" starts the
      // real flow rather than editing a field.
      await signInWithSocial(providerFromAccountKey(accountKey) as any);
    } catch (error) {
      showToastMessage("Failed to reconnect account");
      console.error(error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [accountKey]: false }));
    }
  };
  const handleDeleteAccount = () => {
    setShowDeleteAlert(true);
  };

  const confirmDeleteAccount = async () => {
    setShowDeleteAlert(false);
    setIsLoading(true);
    try {
      await softDeleteAccount();
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;
      // 409 ACCOUNT_ALREADY_DELETED: the account is already gone, so finish signing out.
      if (status !== 409) {
        console.error("Account Deletion Error:", error);
        setIsLoading(false);
        showToastMessage(
          status === 403 && code === "ACCOUNT_DELETION_NOT_ALLOWED"
            ? "This account cannot be deleted from the app."
            : "Failed to delete account. Please try again."
        );
        return;
      }
    }
    setIsLoading(false); // the loading view has no toast, so show the page again first
    showToastMessage("Account deletion initiated");
    await logout();
  };

  const handleBack = () => {
    router.push("/settings", "back");
  };

  const handleEditProfile = () => {
    router.push("/settings/edit-profile");
  };
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // If data is still loading, you could show a spinner here
  if (isLoading) {
    return (
      <IonPage>
        <Header
          showGreeting={false}
          showPageTitle={true}
          showUserIcon={false}
          pageTitle="My Account"
          showBackButton={true}
          onBackButtonClick={handleBack}
        />
        <IonContent className="ion-padding account-content">
          <div className="loading-container">
            <p>Loading account information...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Once data is loaded
  return (
    <IonPage>
      {/* Header with gradient background */}
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="My Account"
        showBackButton={true}
        onBackButtonClick={handleBack}
      />

      <IonContent className="account-content">
        <div className="account-section-header">
          <IonText>
            <h2 className="settings-title">PROFILE</h2>
          </IonText>
        </div>

        <div className="profile-card" onClick={handleEditProfile}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "left",
              textWrap: "balance",
              width: "100%", // <-- Ensure the card content takes full width
              boxSizing: "border-box", // <-- Prevent overflow due to padding
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                width: "100%", // <-- Ensure inner content doesn't overflow
                minWidth: 0, // <-- Allow text to shrink if needed
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%", // <-- Ensure row takes full width
                    minWidth: 0,
                  }}
                >
                  {/* <IonIcon icon={personCircleOutline} /> */}
                  <h2
                    style={{
                      fontFamily: "Figtree",
                      fontWeight: "400",
                      fontSize: "17px",
                      justifyContent: "center",
                      textAlign: "left",
                      lineHeight: "22px",
                      letterSpacing: "0px",
                      color: "#000000",
                      margin: 0,
                      textWrap: "balance",
                      whiteSpace: "nowrap", // Prevents breaking in the middle of a word
                      overflow: "hidden", // Hides overflow
                      textOverflow: "ellipsis", // Adds ... if text is too long
                      maxWidth: "100%", // Ensures it doesn't overflow parent
                    }}
                    title={
                      profile?.first_name || profile?.last_name
                        ? `${profile?.first_name || ""} ${
                            profile?.last_name || ""
                          }`.trim()
                        : "Unknown User"
                    }
                  >
                    {(profile?.first_name || profile?.last_name
                      ? `${profile?.first_name || ""} ${
                          profile?.last_name || ""
                        }`.trim()
                      : "Unknown User"
                    ).length > 22
                      ? (profile?.first_name || profile?.last_name
                          ? `${profile?.first_name || ""} ${
                              profile?.last_name || ""
                            }`.trim()
                          : "Unknown User"
                        ).slice(0, 20) + "..."
                      : profile?.first_name || profile?.last_name
                      ? `${profile?.first_name || ""} ${
                          profile?.last_name || ""
                        }`.trim()
                      : "Unknown User"}
                  </h2>
                </div>
                <IonIcon
                  style={{ color: "#555", fontSize: "25px" }}
                  icon={chevronForward}
                ></IonIcon>
              </div>
              {profile?.email && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <IonIcon
                      icon={mail}
                      style={{ color: "#555", fontSize: "18px" }}
                    />
                    <p className="phone" style={{ margin: 0 }}>
                      {profile?.email}
                    </p>
                  </div>
                </>
              )}
              <hr
                style={{
                  border: "none",
                  height: "1px",
                  width: "auto",
                  backgroundColor: "#E0E0E0",
                }}
              />
              {profile?.phone && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <IonIcon
                      icon={phonePortraitOutline}
                      style={{ color: "#555", fontSize: "18px" }}
                    />
                    <p className="phone" style={{ margin: 0 }}>
                      {formatPhoneNumber(
                        profile?.phone?.replace(
                          profile?.phone_country_code ?? "",
                          ""
                        ),
                        profile?.phone_country_code,
                        true
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Connected Accounts Section */}
        {/* Connected Accounts Section */}
        {/* ⚠️ Rendered from the KERNEL alone — every provider card, always, whether linked
            or not, so the set of cards no longer differs per user. Previously visibility
            keyed off blaze's `<p>_account` key, so a profile that linked only through the
            kernel lost the whole section — and with it the only way to disconnect, exactly
            like the push-notification regression.

            ⚠️ DISPLAY is a UNION of kernel + blaze; ACTIONS are kernel-only. A provider
            connected in blaze before the kernel owned identities is genuinely connected and
            still signs you in, but the kernel has no row for it and Disconnect would 404.
            Kernel-only display would call that "not linked" and tell the user to do
            something they did months ago. See describeLink. */}
        {(
          <>
            <div className="account-section-header">
              <IonText>
                <h2 className="settings-title">CONNECTED ACCOUNTS</h2>
              </IonText>
            </div>

            {(
              <>
                <div className="connected-account-card">
                  <div className="social-connection">
                    <FacebookIcon
                      className="media-icon"
                      style={{
                        fontSize: "24px",
                      }}
                    />
                    <div className="social-info">
                      <h3 className="social-title">Facebook</h3>
                      <p className="social-description">
                        {describeLink("facebook_account", "Facebook")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="action-list">
                  {isMethodLinked("facebook_account") ? (
                    <div
                      className="disconnect-button"
                      onClick={() =>
                        !actionLoading["facebook_account"] &&
                        handleDisconnectAccount("facebook_account")
                      }
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        fontFamily: "Figtree",
                        fontStyle: "normal",
                        fontWeight: "500",
                        fontFeatureSettings: "'case' on",
                        color: "#FF001A",
                        cursor: actionLoading["facebook_account"]
                          ? "not-allowed"
                          : "pointer",
                        opacity: actionLoading["facebook_account"] ? 0.6 : 1,
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {actionLoading["facebook_account"] && (
                          <IonSpinner name="dots" style={{ marginRight: 2 }} />
                        )}
                        Disconnect from Facebook
                      </span>
                      <IonIcon className="text-black" icon={chevronForward} />
                    </div>
                  ) : (
                    <div
                      className="disconnect-button"
                      onClick={() => {
                        // TODO: Implement relink logic here
                        handleReconnectAccount("facebook_account");
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        fontFamily: "Figtree",
                        fontStyle: "normal",
                        fontWeight: "500",
                        fontFeatureSettings: "'case' on",
                        color: "#007AFF",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {actionLoading["facebook_account"] && (
                          <IonSpinner name="dots" style={{ marginRight: 2 }} />
                        )}
                        {linkActionLabel("facebook_account", "Facebook")}
                      </span>
                      <IonIcon className="text-black" icon={chevronForward} />
                    </div>
                  )}
                </div>
              </>
            )}

            {(
              <>
                <div className="connected-account-card">
                  <div className="social-connection">
                    <GoogleIcon
                      className="media-icon"
                      style={{
                        fontSize: "24px",
                      }}
                    />
                    <div className="social-info">
                      <h3 className="social-title">Google</h3>
                      <p className="social-description">
                        {describeLink("google_account", "Google")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="action-list">
                  {isMethodLinked("google_account") ? (
                    <div
                      className="disconnect-button"
                      onClick={() => handleDisconnectAccount("google_account")}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        fontFamily: "Figtree",
                        fontStyle: "normal",
                        fontWeight: "500",
                        fontFeatureSettings: "'case' on",
                        color: "#FF001A",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {actionLoading["google_account"] && (
                          <IonSpinner name="dots" style={{ marginRight: 2 }} />
                        )}
                        Disconnect from Google
                      </span>
                      <IonIcon className="text-black" icon={chevronForward} />
                    </div>
                  ) : (
                    <div
                      className="disconnect-button"
                      onClick={() => {
                        handleReconnectAccount("google_account");
                        // showToastMessage("Relink Google not implemented");
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        fontFamily: "Figtree",
                        fontStyle: "normal",
                        fontWeight: "500",
                        fontFeatureSettings: "'case' on",
                        color: "#007AFF",
                        cursor: "pointer",
                      }}
                    >
                      {" "}
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {actionLoading["google_account"] && (
                          <IonSpinner name="dots" style={{ marginRight: 2 }} />
                        )}
                        {linkActionLabel("google_account", "Google")}
                      </span>
                      <IonIcon className="text-black" icon={chevronForward} />
                    </div>
                  )}
                </div>
              </>
            )}

            {(
              <>
                <div className="connected-account-card">
                  <div className="social-connection">
                    <AppleIcon
                      className="media-icon"
                      style={{
                        fontSize: "24px",
                      }}
                    />
                    <div className="social-info">
                      <h3 className="social-title">Apple</h3>
                      <p className="social-description">
                        {describeLink("apple_account", "Apple")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="action-list">
                  {isMethodLinked("apple_account") ? (
                    <div
                      className="disconnect-button"
                      onClick={() => handleDisconnectAccount("apple_account")}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        fontFamily: "Figtree",
                        fontStyle: "normal",
                        fontWeight: "500",
                        fontFeatureSettings: "'case' on",
                        color: "#FF001A",
                        cursor: "pointer",
                      }}
                    >
                      {" "}
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {actionLoading["apple_account"] && (
                          <IonSpinner name="dots" style={{ marginRight: 2 }} />
                        )}
                        Disconnect from Apple
                      </span>
                      <IonIcon className="text-black" icon={chevronForward} />
                    </div>
                  ) : (
                    <div
                      className="disconnect-button"
                      onClick={() => {
                        // TODO: Implement relink logic here
                        handleReconnectAccount("apple_account");
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        fontFamily: "Figtree",
                        fontStyle: "normal",
                        fontWeight: "500",
                        fontFeatureSettings: "'case' on",
                        color: "#007AFF",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {actionLoading["apple_account"] && (
                          <IonSpinner name="dots" style={{ marginRight: 2 }} />
                        )}
                        {linkActionLabel("apple_account", "Apple")}
                      </span>
                      <IonIcon className="text-black" icon={chevronForward} />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
        <div className="delete-account-container">
          <IonItem
            className="delete-item"
            button
            onClick={handleDeleteAccount}
            lines="none"
            style={{
              margin: "0 auto",
            }}
          >
            <IonLabel
              style={{
                fontFamily: "Figtree",
                fontStyle: "normal",
                fontWeight: "500",
                fontSize: "16px",
                fontFeatureSettings: "'case' on",
                color: "#FF001A",
              }}
            >
              Delete my account
            </IonLabel>
            <IonIcon icon={chevronForward} slot="end" />
          </IonItem>
        </div>
        {/* Logout Button */}
        <div>
          {/* Logout Button */}
          <div className="w-full flex justify-center mt-8 mb-8">
            <button
              className="bg-transparent border-none flex items-center justify-center cursor-pointer text-xl px-4 py-2 rounded-lg transition-colors duration-200 skip-link font-medium min-w-[120px] hover:bg-blue-50"
              onClick={handleLogout}
              style={{ fontFamily: "Figtree" }}
            >
              <IonIcon icon={exitOutline} className="mr-2 text-xxl" />
              Logout
            </button>
          </div>

          {showLogoutAlert && (
            <RequestPopup
              onClose={cancelLogout}
              title="Confirm Logout"
              description="Are you sure you want to log out of your account?"
              buttonText="Logout"
              onProceed={confirmLogout}
            />
          )}
        </div>
      </IonContent>

      {/* Alerts and Toasts */}
      {/* <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Delete Account"
        message="Are you sure you want to delete your account? This action cannot be undone."
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "Delete",
            handler: confirmDeleteAccount,
            cssClass: "danger",
          },
        ]}
      /> */}
      {/* Convert this to a delete my account */}
      {showDeleteAlert && (
        <RequestPopup
          onClose={() => setShowDeleteAlert(false)}
          title="Delete My Account"
          description="This will permanently delete your account and all your information. Are you sure you want to proceed?"
          buttonText="Delete my account"
          onProceed={confirmDeleteAccount}
          buttonClassName="tertiary-button "
        />
      )}
      {showDisconnectAlert && (
        <RequestPopup
          onClose={() => setShowDisconnectAlert(false)}
          title="Disconnect Account"
          description="Are you sure you want to disconnect this account?"
          buttonText="Disconnect account"
          onProceed={confirmDisconnectAccount}
          buttonClassName="tertiary-button"
        />
      )}

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={2000}
        position="bottom"
      />
    </IonPage>
  );
};

export default MyAccountPage;
